import axios from "axios";
import axiosRetry from "axios-retry";
import { Toast } from "react-vant";
import { isTokenExpired, refreshToken, navigateToSignIn } from "./tokenTools";
import { isNonEmptyArray, isNonEmptyObject } from "./objectTools";
import { showDeviceModal } from "@/views/mine/device-manage/components/device-modal";
import DSBridge from "dsbridge";
import { showCustomToast } from "@/views/components/CustomToast";

const MAX_REFRESH_ATTEMPTS = 1;
let refreshAttempts = 0;
const globalCacheData = {};
const ajax = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 30000,
  withCredentials: false,
});
// 避免同时发送多个刷新token的请求
let isRefreshing = false;
// 在这里添加 axios-retry 插件，指定重试策略
axiosRetry(ajax, {
  retries: 3, // 重试次数
  retryDelay: (retryCount) => {
    return retryCount * 1000; // 重试延迟，可以根据需要自定义
  },
  shouldResetTimeout: true, // 重试时是否重置超时时间
});

// 在请求拦截器中设置JWT令牌
ajax.interceptors.request.use(async (config) => {
  const token = localStorage.getItem("access_token");
  const useToken = config.url.includes("register") ? false : true;
  if (token && useToken) {
    if (isTokenExpired()) {
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const newToken = await refreshToken();
          config.headers.Authorization = `Bearer ${newToken}`;
          isRefreshing = false;
        } catch (e) {
          if (e === "无刷新Token") {
            refreshAttempts = 1;
            DSBridge.call("auth.logout");
            localStorage.clear();
            navigateToSignIn("#/login/logon");
          }
        }
      }
    } else {
      const token = localStorage.getItem("access_token");
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

ajax.interceptors.response.use(
  async (response) => {
    //原生数据或者其他问题
    if (!response?.data?.code) {
      return response?.data || response;
    }
    // 多个登录错误
    if (response.data.code == 422) {
      showDeviceModal({
        message: response.data.message,
        onConfirm: () => {
          DSBridge.call("auth.logout");
          localStorage.clear();
          navigateToSignIn("#/login/logon");
        },
        onClose: () => {
          localStorage.clear();
          navigateToSignIn("#/main/home");
        },
        isShowConfirm: true,
        confirmText: "重新登录",
      });
    }
    //登录页弹窗
    if (response.data.code == 423) {
      showCustomToast(response.data?.message);
    }
    //鉴权错误
    if (response.data.code == 402) {
      if (refreshAttempts < MAX_REFRESH_ATTEMPTS) {
        refreshAttempts++;
        try {
          const newToken = await refreshToken();
          if (newToken) {
            const originalRequest = response.config;
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return ajax(originalRequest);
          }
        } catch {
          console.log("刷新token失败");
        }
      } else {
        refreshAttempts = 0;
        Toast.fail(`${response.data.message} 请重新登录`);
        DSBridge.call("auth.logout");
        localStorage.clear();
        navigateToSignIn("#/login/logon");
      }
    }
    //弹错误消息
    if (
      response.data.code != 200 &&
      response.data.code != 402 &&
      response.data.code != 422 &&
      response.data.code != 423
    ) {
      try {
        JSON.parse(response.data.message);
        const mes = JSON.parse(response.data.message.replace(/'/g, '"'));
        const values = Object.values(mes);
        const firstValue = values[0];
        if (response.config.toast !== "hideToast") {
          Toast.fail({
            message: firstValue,
            duration: 3000,
          });
        }
      } catch {
        if (response.config.toast !== "hideToast") {
          Toast.fail({
            message: response.data.message,
            duration: 3000,
          });
        }
      }
    }
    //缓存有效期为 5 分钟
    if (isNonEmptyObject(response.data) || isNonEmptyArray(response.data)) {
      globalCacheData[response.config.url] = {
        data: response.data,
        time: new Date().getTime() + 300000,
      };
    }
    return response.data;
  },
  (error) => {
    //能走这里 一般都是 本地问题
    Toast.fail("当前网络不稳定!");
    return Promise.reject("当前网络不稳定!");
  }
);
const cacheData = (args) => {
  let [url] = args;
  let nocache = false;
  if (typeof url === "object" && "url" in url) {
    url = url.url;
  }
  if (args.includes("nocache")) {
    nocache = true;
  }
  // 检查配置对象中的 cache 属性
  for (let arg of args) {
    if (typeof arg === "object" && arg.cache === "nocache") {
      nocache = true;
      break;
    }
  }
  if (
    url &&
    !nocache &&
    globalCacheData[url] &&
    globalCacheData[url]["time"] > new Date().getTime()
  ) {
    return globalCacheData[url]["data"];
  }
  return false;
};
const request = {
  get() {
    const args = Array.from(arguments);
    const cache = cacheData(args);
    if (cache) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(cache);
        }, 100);
      });
    }
    
    const url = args[0];
    
    if (args.length === 2) {
      if (args[1] === "nocache") {
        return ajax.get(url);
      } else {
        return ajax.get(url, args[1]);
      }
    } else if (args.length === 3 && args[1] === "nocache") {
      return ajax.get(url, args[2]);
    } else {
      return ajax.get(url);
    }
  },
  
  post() {
    const args = Array.from(arguments);
    const cache = cacheData(args);
    if (cache) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(cache);
        }, 100);
      });
    }
    
    const url = args[0];

    if (args.length === 2 && args[1] === "nocache") {
      return ajax.post(url);
    } else if (args.length === 2) {
      return ajax.post(url, args[1]);
    } else if (args.length === 3) {
      if (args[2] === "nocache") {
        return ajax.post(url, args[1]);
      } else {
        return ajax.post(url, args[1], args[2]);
      }
    } else if (args.length === 4 && args[2] === "nocache") {
      return ajax.post(url, args[1], args[3]);
    } else {
      return ajax.post(url, args[1]);
    }
  },
  
  delete() {
    const args = Array.from(arguments);
    const cache = cacheData(args);
    if (cache) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(cache);
        }, 100);
      });
    }
    
    const url = args[0];
    
    if (args.length === 2) {
      if (args[1] === "nocache") {
        return ajax.delete(url);
      } else {
        return ajax.delete(url, args[1]);
      }
    } else if (args.length === 3 && args[1] === "nocache") {
      return ajax.delete(url, args[2]);
    } else {
      return ajax.delete(url);
    }
  },
};

export default request;
